var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { DropdownButtonContext, DropdownSelect, Icon, SelectItem, Tooltip, } from '@dixa/lib-shared-habla';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { shallowEqual } from 'react-redux';
import { queuesApi } from 'apis/queues';
import { appActions } from 'app/app.actions';
import { analytics } from 'app/utils/segment';
import { CommandKeybindingsTooltipContent } from 'components/commandKeybindingsTooltipContent';
import { notificationActions } from 'components/notifications/actions/notification';
import { transferToQueueActions } from 'components/shared/transferToQueueModal/actions/transferToQueueModal';
import { useLazyPaginatedEndpoint } from 'utils/apis/pagination/useLazyPaginatedEndpoint';
import { useCommandHandler } from 'utils/commands';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { bindActionToPromise } from 'utils/sagaApiGeneratorHelpers';
import styles from './transferToQueueDropdown.module.scss';
const skipConfirmModalKey = 'transferToQueueDropdownSkipConfirmModal';
export const TransferToQueueDropdown = () => {
    const dispatch = useAppDispatch();
    const [searchQuery, setSearchQuery] = useState();
    const { arg, asyncStatus, changeArg, items } = useLazyPaginatedEndpoint({
        endpoint: queuesApi.endpoints.readQueues,
        itemsSelector: ({ data: { queues } }) => queues,
        infinite: true,
    });
    const [selectedQueue, csid, transferToQueueRequests] = useAppSelector((state) => [
        state.conversation.metaConversation.conversation.queue,
        state.conversation.metaConversation.conversation.id,
        state.transferToQueue.requests,
    ], shallowEqual);
    useEffect(() => {
        if (searchQuery !== (arg === null || arg === void 0 ? void 0 : arg.searchTerm)) {
            changeArg({
                searchTerm: searchQuery,
                offset: undefined,
                limit: undefined,
            }, {
                debounceTime: 300,
            });
        }
        // Don't rely on arg as dependency because we change the arg inside.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [changeArg, searchQuery]);
    const isTransferringToQueue = !!transferToQueueRequests.find((request) => request.csid === csid);
    const handleQueueOnSelect = useCallback((selectQueue) => __awaiter(void 0, void 0, void 0, function* () {
        analytics.conversationHeaderTransferToQueueItemSelected({
            queue: selectQueue.name,
        });
        if (localStorage.getItem(skipConfirmModalKey) !== 'true') {
            const { isConfirmed, isCheckboxChecked } = yield dispatch(appActions.showModal({
                modal: {
                    type: 'app:confirm',
                    arg: {
                        id: 'transferToQueue',
                        title: 'Transfer conversation',
                        description: 'Are you sure you want to transfer this conversation?',
                        confirm: {
                            variant: 'primary',
                        },
                        checkbox: {
                            label: 'Do not show this message again.',
                        },
                    },
                },
            }));
            if (isConfirmed && isCheckboxChecked) {
                localStorage.setItem(skipConfirmModalKey, 'true');
            }
            // do not do any further action if the user didn't confirm
            if (!isConfirmed) {
                return;
            }
        }
        const transferToQueue = (csid, toQueue) => __awaiter(void 0, void 0, void 0, function* () {
            const { transferToQueueModalRequest } = transferToQueueActions;
            try {
                yield bindActionToPromise(dispatch, transferToQueueModalRequest)({
                    csid,
                    requestToQueue: toQueue,
                });
                analytics.conversationTransferred({
                    conversationId: csid,
                    option: 'queue',
                });
            }
            catch (error) {
                console.warn(error);
                dispatch(notificationActions.notificationAdd({
                    title: 'Could not transfer the conversation',
                    text: `An issue occurred while trying transfer conversation ${csid}.`,
                    status: 'danger',
                    icon: 'exclamation',
                }));
            }
        });
        setSearchQuery(undefined);
        if (csid) {
            transferToQueue(csid, selectQueue);
        }
    }), [csid, setSearchQuery, dispatch]);
    const handleOnToggle = useCallback((isOpen) => {
        if (isOpen) {
            analytics.conversationHeaderTransferToQueueMenuOpened();
            changeArg({ sort: { key: 'name' }, is_archived: false });
        }
        else {
            setSearchQuery(undefined);
        }
    }, [setSearchQuery, changeArg]);
    const selectedQueueItem = useMemo(() => items.find(({ id }) => id === (selectedQueue === null || selectedQueue === void 0 ? void 0 : selectedQueue.id)), [items, selectedQueue]);
    const emptyMessage = asyncStatus === 'pending'
        ? 'Loading queues...'
        : asyncStatus === 'rejected'
            ? 'A problem occured'
            : (searchQuery === null || searchQuery === void 0 ? void 0 : searchQuery.length)
                ? 'No search results'
                : 'No queues';
    const tooltipContent = (_jsx(CommandKeybindingsTooltipContent, Object.assign({ commandName: "conversation:transferToQueue" }, { children: "Transfer to queue" })));
    return (_jsx(Tooltip, Object.assign({ dark: true, content: tooltipContent, disabled: isTransferringToQueue, wrap: "div", wrapProps: {
            className: styles.tooltip,
        } }, { children: _jsxs(DropdownSelect, Object.assign({ "data-testid": "conversation-transfer-to-queue-dropdown", items: items, selectedItems: selectedQueueItem ? [selectedQueueItem] : undefined, itemComponent: SelectItem, buttonProps: {
                variant: 'tertiary',
                disabled: isTransferringToQueue,
                slotStart: _jsx(Icon, { name: "flow" }),
            }, onSelect: handleQueueOnSelect, onToggle: handleOnToggle, hasSearch: true, onSearch: (value) => setSearchQuery(value !== undefined ? value : ''), noArrow: isTransferringToQueue, emptyMessage: emptyMessage, fixed: true, className: styles.dropdownSelect, dropdownProps: {
                className: styles.dropdown,
            } }, { children: [_jsx(DropdownButtonContext.Consumer, { children: (props) => _jsx(TransferToQueueCommandHandler, Object.assign({}, props)) }), isTransferringToQueue
                    ? 'Transfer in progress...'
                    : (selectedQueue === null || selectedQueue === void 0 ? void 0 : selectedQueue.name) || 'Not in queue'] })) })));
};
const TransferToQueueCommandHandler = ({ dropdownOpen, openDropdown, closeDropdown, }) => {
    useCommandHandler({
        name: 'conversation:transferToQueue',
        actionFn: () => {
            // We have to do this as toggleDropdown doesn't seem to work in this case.
            if (dropdownOpen) {
                closeDropdown();
            }
            else {
                openDropdown();
            }
        },
    });
    return null;
};
